import { CommonHelper } from "../../../react-core/Helpers/CommonHelper";
import { JsonDateParser } from "../../../react-core/Utils/JsonDateParser";
import { UserClaim } from "../../../Wrap/Constants/Enums/UserClaim";
import { ProcessType } from "../../Shared/IST/Resource/Content";
import { checkPermission } from "../../Shared/Permission/PermissionHelper";
import { TransactionEnum } from "../Components/Clients/PortfolioDashboard/Components/Transactions/Resource/Contents";

export class StaticUrls {
    public static EmptyUrl = "javascript:void(0);";
    public static Withdraw = "Withdraw";
    public static Deposit = "Deposit";
    public static SinglePayment = "Single payment";
    public static RegularPayment = "Add regular payment";
    public static RegularPaymentEdit = "Edit regular payment";
    public static Buy = "Buy";
    public static Sell = "Sell";
    public static Switch = "Switch";
    public static PensionBenefits = "Take pension benefits";
    public static Topup = "Top-up with Immediate tax-free lump sum";
    public static IncomePaymentOneOff = "One-off income payment";
    public static ClientDashboard = "Client dashbaord";
    public static UpdateLifetimeAllowance = "Update Allowance Details";
    public static WhatIfTool = "Launch 'What if' Tool";
    public static NoChangeTool = "Launch 'No change' Tool";
    public static LaunchApps = "Launch Apps";
    public static EditIncomeWithdrawals = "Edit Income Payments";
    public static EditIncomeDrawdown = "Edit Income Drawdown";
    public static CancelRegularCrystallisation = "Cancel tailored drawdown";
    public static EditGadReview = "Gad Review";
    public static ConvertCappedToFlexi = "Convert Capped to Flexible Drawdown";
    public static PPToSsIsa = "PP to S&S ISA";
    public static ppToAbrdnSIPP = "PP to Aberdeen SIPP";


    private static today = JsonDateParser.getFormatDateString(new Date(), "yyyy-MM-dd");

    public static WithdrawUrl(clAccountId: string): string {
        return `/clients/cash/regularwithdrawals.aspx?claccountid=${clAccountId}`;
    }

    public static NBEC(clAccountId: string, bondonly = false, instructionId = 0): string {
        return `/clients/newbusiness/newtransfer.aspx?filter=newbusinessexistingclient&needclient=1&claccountid=${clAccountId}&asatdate=${this.today}&todate=${this.today}&bondonly=${bondonly}&instructionId=${instructionId}`;
    }

    public static NBECBond(clAccountId: string, instructionId = 0, wizardSessionId: number): string {
        return `/Clients/ChangeContribution/TaxPlanning.aspx?wizardsessionid=${wizardSessionId}&claccountid=${clAccountId}&instructionid=${instructionId}`;
    }

    public static NBECSIPP(clAccountId: string, instructionId = 0, wizardSessionId: number): string {
        return `/clients/transfers/Assets1.aspx?wizardsessionid=${wizardSessionId}&claccountid=${clAccountId}&rtype=&bondonly=false&instructionid=${instructionId}`;
    }

    public static SippDetail(clAccountId: string): string {
        return `/clients/holdings/holdings-sippdetail.aspx?claccountid=${clAccountId}&searchresultsurl=https%3A%2F%2Fslrebrandacct.fnz.co.uk%2Fclients%2Fholdings%2Fholdings-summarynoclient.aspx%3Fusermsg%3DQ1F6NC81dFNpSXpHVnZkMCtETmJlS2EyUXpuZWVWYmsvWnFDS21UdmFXMlhwaVdqMFVRdmx3Q254ZzVJNUxiSHRGelV5bmI2VFk2TG1Cck5PT1BkdUE9PQ%3D%3D`;
    }

    public static CreateOrder(clAccountId: string): string {
        if (!checkPermission(UserClaim.AdviserPortalBuySellSwitchReadWrite)) {return null;}
        return `/clients/orders/orders-mixed-setup.aspx?claccountid=${clAccountId}&asatdate=${this.today}`;
    }

    public static DepositUrl(
        clAccountId: string,
        actionType: string,
        cashId: string,
        subType?: string,
        instructionType?: string,
        override?: string
    ): string {
        let url = `/clients/cash/Cash-Deposit.aspx?actiontype=${actionType}&claccountid=${clAccountId}&cashid=${cashId}`;
        if (subType != null) {
            url = url + `&subtype=${subType}`;
        }
        if (instructionType != null) {
            url = url + `&InstructionType=${instructionType}`;
        }
        if (override != null) {
            url = url + `&override=${override}`;
        }
        return url;
    }

    public static QuoteReadyUrl(customerId: string, instructionId: string): string {
        return `/wpadviser/en-gb/dashboard/home/main/client/new-quote/quote-ready?customerid=${customerId}&id=${instructionId}`;
    }

    public static FullClientDetailsUrl(customerId: string, instructionId: string, isReview?: boolean): string {
        const landOn = isReview ? "review" : "full-client-detail";
        return `/wpadviser/en-gb/dashboard/home/main/client/new-business/${landOn}?customerid=${customerId}&id=${instructionId}`;
    }

    public static ClientDashboardUrl(hierarchyId: string, tab?: string): string {
        return `/wpadviser/en-gb/dashboard/home/main/customer/${hierarchyId}/${tab}`;
    }

    public static WrapperSelectionUrl(customerId: string, instructionId: string, processType: ProcessType): string {
        if (processType == ProcessType.Quote) {
            return `/wpadviser/en-gb/dashboard/home/main/client/new-quote/wrapper-selection?customerid=${customerId}&id=${instructionId}`;
        }
    }
    public static QuoteWrapperSelectionUrlCustomer(customerId: string): string {
        return `/wpadviser/en-gb/dashboard/home/main/client/new-quote/wrapper-selection?customerid=${customerId}`;
    }

    public static SinglePaymentUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        if (!checkPermission(UserClaim.AdviserPortalSPTAndRegularPaymentReadWrite)) {return null;}
        const landOn = isReview ? "review" : "payments-in";
        return `/wpadviser/en-gb/dashboard/home/main/client/single-payment-transfer/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static BuyUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string
    ) {
        if (!checkPermission(UserClaim.AdviserPortalBuySellSwitchReadWrite)) {return null;}
        return `/wpadviser/en-gb/dashboard/home/main/client/buy/order-setup?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static RegularPaymentUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        action: "add" | "edit" = "add",
        clientBatchId: number = null,
        isReview?: boolean
    ) {
        if (!checkPermission(UserClaim.AdviserPortalSPTAndRegularPaymentReadWrite)) {return null;}
        const landOn = isReview ? "review" : "payments-in";
        return `/wpadviser/en-gb/dashboard/home/main/client/regular-payment/${landOn}?${
            clientBatchId ? `batchid=${clientBatchId}&` : ""
        }action=${action}&${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static WhatIfToolUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        clientBatchId: number = null,
        isReview?: boolean
    ) {
        //TODO : temp fix need to double check the permission with SC
        if (!checkPermission(UserClaim.AdviserPortalSPTAndRegularPaymentReadWrite)) {return null;}
        const landOn = isReview ? "review" : "wrapper-selection";
        return `/wpadviser/en-gb/dashboard/home/main/client/whatiftool/${landOn}?${
            clientBatchId ? `batchid=${clientBatchId}&` : ""
        }${this.getParamsForSubAccountWizard(type, clAccountId, headAccountHierarchyId, subAccountHierarchyId)}`;
    }

    public static NoChangeToolUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        if (!checkPermission(UserClaim.AdviserPortalSPTAndRegularPaymentReadWrite)) {return null;}
        return `/wpadviser/en-gb/dashboard/home/main/client/nochangetool/review?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static SwitchUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        if (!checkPermission(UserClaim.AdviserPortalBuySellSwitchReadWrite)) {return null;}
        const landOn = isReview ? "review" : "order-setup";
        return `/wpadviser/en-gb/dashboard/home/main/client/switch/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static SellUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string
    ) {
        if (!checkPermission(UserClaim.AdviserPortalBuySellSwitchReadWrite)) {return null;}
        return `/wpadviser/en-gb/dashboard/home/main/client/sell/order-setup?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static PensionBenefitsUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        //TODO confirm with Dan if we need to do this now
        const landOn = isReview ? "review" : "pension-benefits-details";
        return `/wpadviser/en-gb/dashboard/home/main/client/pension-benefits/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static TopupUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        //TODO confirm with Dan if we need to do this now
        const landOn = isReview ? "review" : "payments-in";
        return `/wpadviser/en-gb/dashboard/home/main/client/top-up-with-itfls/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static IncomePaymentOneOffUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "incomepayment-detail";
        return `/wpadviser/en-gb/dashboard/home/main/client/incomepayment-oneoff/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static EditIncomeWithdrawalsUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "income-payment";
        return `/wpadviser/en-gb/dashboard/home/main/client/edit-income-withdrawals/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static EditIncomeDrawdownUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "drawdown-detail";
        return `/wpadviser/en-gb/dashboard/home/main/client/edit-income-drawdown/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static CancelRegularCrystallisationUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string
    ) {
        return `/wpadviser/en-gb/dashboard/home/main/client/cancel-regular-crystallisation/review?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    public static EditGadReviewUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean,
        arrangementId?: number
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "income-payment";
        return `/wpadviser/en-gb/dashboard/home/main/client/gad-review/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}&arrangementid=${arrangementId}`;
    }

    public static BedAndProductUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean,
        arrangementId?: number
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "investment-details";
        return `/wpadviser/en-gb/dashboard/home/main/client/pp-to-product/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}&arrangementid=${arrangementId}`;
    }

    public static BedAndAbrdnProductUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean,
        arrangementId?: number
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "investment-details";
        return `/wpadviser/en-gb/dashboard/home/main/client/pp-to-AbrdnSIPP/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}&arrangementid=${arrangementId}`;
    }

    public static ConvertCappedToFlexiUrl(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        isReview?: boolean
    ) {
        //TODO Pension stuff need build in pension permission
        const landOn = isReview ? "review" : "select-arrangement";
        return `/wpadviser/en-gb/dashboard/home/main/client/convert-capped-to-flexi/${landOn}?${this.getParamsForSubAccountWizard(
            type,
            clAccountId,
            headAccountHierarchyId,
            subAccountHierarchyId
        )}`;
    }

    private static getParamsForSubAccountWizard(
        type: string,
        clAccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string
    ) {
        const optionalType = CommonHelper.isNullOrEmpty(type) ? "" : `type=${type}&`;
        return optionalType + `claccountid=${clAccountId}&hierarchyid=${headAccountHierarchyId}${
            subAccountHierarchyId === "" ? "" : `&subaccounthierarchyid=${subAccountHierarchyId}`
        }`;
    }

    public static UpdateLifetimeAllowanceUrl(headAccountHierarchyId: string, subAccountHierarchyId: string) {
        //TODO pension stuff, need build with pension permission build
        if (subAccountHierarchyId === "")
            {return `/wpadviser/en-gb/dashboard/home/main/client/update-lifetime-allowance/update-lifetime-allowance-detail?hierarchyid=${headAccountHierarchyId}`;}
        return `/wpadviser/en-gb/dashboard/home/main/client/update-lifetime-allowance/update-lifetime-allowance-detail?hierarchyid=${subAccountHierarchyId}`;
    }
    public static ppToSsIsaUrl(
        subClaccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        customerId: string
    ) {
        return `/wpadviser/en-gb/dashboard/home/main/client/pp-to-product/investment-Details?claccountid=${subClaccountId}&hierarchyid=${headAccountHierarchyId}&subAccountHierarchyId=${subAccountHierarchyId}&customerid=${customerId}`;
    }

    public static ppToAbrdnSIPPUrl(
        subClaccountId: string,
        headAccountHierarchyId: string,
        subAccountHierarchyId: string,
        customerId: string
    ) {
        return `/wpadviser/en-gb/dashboard/home/main/client/pp-to-AbrdnSIPP/investment-Details?claccountid=${subClaccountId}&hierarchyid=${headAccountHierarchyId}&subAccountHierarchyId=${subAccountHierarchyId}&customerid=${customerId}`;
    }

    public static createClientPortfolioReportUrl(levelHierarchyId: string, headaccounthierarchyId: string) {
        return `/wpadviser/en-gb/dashboard/home/main/customer/${levelHierarchyId}/headaccount/${headaccounthierarchyId}/clientreport`;
    }

    public static transactionViewsUrls(hierarchyId: string, view: TransactionEnum, headaccounthierarchyId: string) {
        return `/wpadviser/en-gb/dashboard/home/main/customer/${hierarchyId}/transactions?transtypeselect=${view}&hierarchyid=${headaccounthierarchyId}`;
    }

    public static reviewCompleteUrl(wizardSessionId: string, clAccountId: string) {
        return `/clients/review/Complete.aspx?wizardsessionid=${wizardSessionId}&claccountid=${clAccountId}`;
    }

    public static completedWizardUrl(wizardSessionId: string, clAccountId: string) {
        return `/clients/wizards/CompleteWizard.aspx?wizardsessionid=${wizardSessionId}&claccountid=${clAccountId}`;
    }

    public static reregistrationTransfersUrl(clAccountId: string) {
        return `/clients/tracking/reregistration-transfers.aspx?claccountid=${clAccountId}&txtStatus=Open`;
    }

    public static reviewRegularReviewRebalanceCompleteUrl(wizardSessionId: string, clAccountId: string) {
        return `/clients/review/CompleteRebalance.aspx?wizardsessionid=${wizardSessionId}&claccountid=${clAccountId}`;
    }
}
